import { useState } from 'react';
import {
  requestCmsCode,
  loginCmsWithPassword,
  saveCmsSession,
  setInitialInstructorPassword,
  resetInstructorPassword
} from '../../utils/auth';
import { generateCode, storeOtp } from '../../utils/otp';

const API_URL = process.env.REACT_APP_API_URL;

export default function CmsLogin({ onLogin }) {
  const [stage, setStage] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [instructor, setInstructor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function clearCredentials() {
    setCode('');
    setPassword('');
    setConfirmPassword('');
  }

async function sendCode(targetInstructor) {
  const otp = generateCode();

  const accountEmail =
    String(
      targetInstructor?.email || ''
    ).trim();

  const deliveryEmail =
    String(
      email || ''
    ).trim();

  if (!accountEmail) {
    throw new Error(
      'No email address is configured for this instructor.'
    );
  }

  if (!deliveryEmail) {
    throw new Error(
      'Enter an email address.'
    );
  }

  /*
   * Store the OTP against the canonical
   * instructor account email. The backend
   * verifies against this value.
   */
  await storeOtp(
    accountEmail,
    otp
  );

  /*
   * Send to the address the instructor
   * actually entered. This allows aliases
   * and avoids unnecessarily routing an
   * email through another account first.
   */
  await fetch(API_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: {
      'Content-Type':
        'application/json'
    },
    body: JSON.stringify({
      action: 'sendOtp',
      email: deliveryEmail,
      code: otp
    })
  });
}

  async function finishPasswordLogin(
    targetInstructor,
    loginPassword
  ) {
    const result = await loginCmsWithPassword(
      targetInstructor.id,
      loginPassword
    );

    const session = saveCmsSession(
      result.instructor,
      result.token
    );

    onLogin(session);
  }

  async function handleEmailSubmit(event) {
    event.preventDefault();

    setError('');
    setLoading(true);
    clearCredentials();

    try {
      const found = await requestCmsCode(email);

      setInstructor(found);

      if (found.password_set === true) {
        setStage('password');
      } else {
        await sendCode(found);
        setStage('setup');
      }
    } catch (err) {
      setError(
        err?.message ||
        'Could not continue.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordLogin(event) {
    event.preventDefault();

    setError('');
    setLoading(true);

    try {
      await finishPasswordLogin(
        instructor,
        password
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not sign in.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleInitialSetup(event) {
    event.preventDefault();

    setError('');

    if (password.length < 8) {
      setError(
        'Password must be at least 8 characters.'
      );
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await setInitialInstructorPassword(
        instructor.id,
        code,
        password
      );

      await finishPasswordLogin(
        {
          ...instructor,
          password_set: true
        },
        password
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not set password.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    setError('');
    setLoading(true);
    clearCredentials();

    try {
      await sendCode(instructor);
      setStage('reset');
    } catch (err) {
      setError(
        err?.message ||
        'Could not send reset code.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(event) {
    event.preventDefault();

    setError('');

    if (password.length < 8) {
      setError(
        'Password must be at least 8 characters.'
      );
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await resetInstructorPassword(
        instructor.id,
        code,
        password
      );

      await finishPasswordLogin(
        instructor,
        password
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not reset password.'
      );
    } finally {
      setLoading(false);
    }
  }

  function backToEmail() {
    setStage('email');
    setInstructor(null);
    clearCredentials();
    setError('');
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <h1 style={styles.title}>
          CMS Login
        </h1>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {stage === 'email' && (
          <form onSubmit={handleEmailSubmit}>
            <label style={styles.label}>
              Email address
            </label>

            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={event =>
                setEmail(event.target.value)
              }
              placeholder="your@email.com"
              autoComplete="email"
              autoFocus
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Checking...'
                : 'Continue'}
            </button>
          </form>
        )}

        {stage === 'password' && (
          <form onSubmit={handlePasswordLogin}>
            <p style={styles.sub}>
              Welcome back
              {instructor?.name
                ? `, ${instructor.name}`
                : ''}
              .
            </p>

            <label style={styles.label}>
              Password
            </label>

            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={event =>
                setPassword(event.target.value)
              }
              autoComplete="current-password"
              autoFocus
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Signing in...'
                : 'Sign In'}
            </button>

            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={handleForgotPassword}
              disabled={loading}
            >
              Forgot password?
            </button>

            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={backToEmail}
              disabled={loading}
            >
              ← Use a different email
            </button>
          </form>
        )}

        {stage === 'setup' && (
          <form onSubmit={handleInitialSetup}>
            <p style={styles.sub}>
              A verification code has been sent to{' '}
              <strong>{email}</strong>.
              Enter it below and choose a password
              for your account.
            </p>

            <label style={styles.label}>
              Verification code
            </label>

            <input
              style={styles.input}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={event =>
                setCode(event.target.value)
              }
              placeholder="000000"
              autoFocus
              required
            />

            <label style={styles.label}>
              New password
            </label>

            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={event =>
                setPassword(event.target.value)
              }
              placeholder="At least 8 characters"
              autoComplete="new-password"
              required
            />

            <label style={styles.label}>
              Confirm password
            </label>

            <input
              style={styles.input}
              type="password"
              value={confirmPassword}
              onChange={event =>
                setConfirmPassword(
                  event.target.value
                )
              }
              placeholder="Repeat password"
              autoComplete="new-password"
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Setting password...'
                : 'Set Password & Sign In'}
            </button>

            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={backToEmail}
              disabled={loading}
            >
              ← Back
            </button>
          </form>
        )}

        {stage === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <p style={styles.sub}>
              A reset code has been sent to{' '}
              <strong>{email}</strong>.
            </p>

            <label style={styles.label}>
              Verification code
            </label>

            <input
              style={styles.input}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={event =>
                setCode(event.target.value)
              }
              placeholder="000000"
              autoFocus
              required
            />

            <label style={styles.label}>
              New password
            </label>

            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={event =>
                setPassword(event.target.value)
              }
              placeholder="At least 8 characters"
              autoComplete="new-password"
              required
            />

            <label style={styles.label}>
              Confirm password
            </label>

            <input
              style={styles.input}
              type="password"
              value={confirmPassword}
              onChange={event =>
                setConfirmPassword(
                  event.target.value
                )
              }
              placeholder="Repeat password"
              autoComplete="new-password"
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Resetting...'
                : 'Reset Password & Sign In'}
            </button>

            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={() => {
                setStage('password');
                clearCredentials();
                setError('');
              }}
              disabled={loading}
            >
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: '100vh',
    background: '#f4f6f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px'
  },

  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '36px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
  },

  title: {
    margin: '0 0 24px',
    fontSize: '22px',
    color: '#1f2933'
  },

  sub: {
    margin: '0 0 16px',
    color: '#444',
    fontSize: '14px',
    lineHeight: '1.5'
  },

  error: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '6px',
    color: '#1f2933'
  },

  input: {
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '16px',
    marginBottom: '14px',
    boxSizing: 'border-box'
  },

  btn: {
    width: '100%',
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '11px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '10px'
  },

  secondaryBtn: {
    width: '100%',
    background: 'transparent',
    color: '#66788a',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '8px'
  }
};
